import {TFunction} from 'i18next';

type TDurationData<T = number> = {
  days: T;
  hours: T;
  minutes: T;
  seconds: T;
};

type TDuration = string;

const parseISODuration = (
  duration: TDuration,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  {defaultValue = 0, shouldMapHours = true}: {defaultValue?: any; shouldMapHours?: boolean} = {},
): TDurationData => {
  const find = (key: string) => {
    const match = duration.match(new RegExp(`(\\d+)${key}`));

    return match === null ? defaultValue : Number(match[1]);
  };

  const data = {
    days: find('D'),
    hours: find('H'),
    minutes: find('M'),
    seconds: find('S'),
  };

  if (shouldMapHours && find('H') >= 24) {
    data.days = (data.days || 0) + Math.floor(find('H') / 24);
    data.hours %= 24;
  }

  return data;
};

const parseISODurationDate = (duration: TDuration): Date => {
  const {days, hours, minutes, seconds} = parseISODuration(duration);

  return new Date(0, 0, days, hours, minutes, seconds);
};

const displayISODuration = (
  t: TFunction,
  duration: TDuration,
  timePrefix: string = 'Common:time',
): string =>
  Object.entries(parseISODuration(duration, {defaultValue: null}))
    .filter(([, value]) => value !== null && value >= 0)
    .map(([key, value]) => `${value} ${t(`${timePrefix}:${key}`, {count: value})}`)
    .join(' ');

const getISODurationSeconds = (duration: TDuration): number => {
  const parsed = parseISODuration(duration);
  const hours = parsed.days * 24 + parsed.hours;
  const minutes = hours * 60 + parsed.minutes;

  return minutes * 60;
};

const makeISODuration = (
  {days, hours, minutes, seconds}: Partial<TDurationData<number | string>>,
  {includeZeroes = false}: {includeZeroes?: boolean} = {},
): TDuration => {
  const display = (value: string | number | undefined, key: string) => {
    if (value === undefined) {
      return '';
    }

    if (Number(value) > 0 || includeZeroes) {
      return `${value}${key}`;
    }

    return '';
  };

  const hasTimeComponents = Boolean(Number(hours) || Number(minutes) || Number(seconds));

  return [
    'P',
    display(days, 'D'),
    hasTimeComponents ? 'T' : '',
    display(hours, 'H'),
    display(minutes, 'M'),
    display(seconds, 'S'),
  ].join('');
};

const makeISODurationFromDate = (date: Date): TDuration =>
  makeISODuration({
    days: date.getDay(),
    hours: date.getHours(),
    minutes: date.getMinutes(),
    seconds: date.getSeconds(),
  });

export {
  parseISODuration,
  makeISODuration,
  getISODurationSeconds,
  displayISODuration,
  parseISODurationDate,
  makeISODurationFromDate,
  TDuration,
  TDurationData,
};
